import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, type LucideIcon } from "lucide-react";
import { useUnreadChatCount } from "@/features/chat/api/useChatThreads";
import { useAppSelector } from "@/stores/hook";
import { cx } from "@/features/Dashboard/v2/ui/cx";
import { M } from "../theme";
import { rememberSegment, rememberedSegment, useMobileNav, type Segment, type TabDef, type TabKey } from "./navModel";

/**
 * The floating bottom bar (the app's XaleBar): a 64px pill inset 14px from
 * the sides. MAIN mode is the tabs; tapping a tab that has segments goes to
 * its remembered segment and swaps the bar to HUB mode — a back puck, a
 * divider and that tab's segments as chips. The capsule under the active
 * item slides between cells.
 */
export const BAR_SLOT = 78;

function Cell({
  icon: Icon,
  label,
  active,
  dot,
  onClick,
  className,
}: {
  icon: LucideIcon;
  label: string;
  active: boolean;
  dot?: boolean;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      aria-label={label}
      className={cx(
        "xm-chip relative z-10 flex h-16 flex-col items-center justify-center gap-1 transition-transform duration-150 active:scale-[0.92]",
        className,
      )}
    >
      <span className="relative">
        <Icon size={20} strokeWidth={active ? 2.2 : 1.7} color={active ? M.accent : M.textFaint} />
        {dot ? (
          <span
            className="absolute -right-1 -top-0.5 size-2 rounded-full ring-[1.5px] ring-white"
            style={{ backgroundColor: M.danger }}
            aria-label="Unread"
          />
        ) : null}
      </span>
      <span
        className="text-[10px] leading-none tracking-[-0.1px]"
        style={{ color: active ? M.accent : M.textFaint, fontWeight: active ? 600 : 500 }}
      >
        {label}
      </span>
    </button>
  );
}

/** The sliding capsule: measured from the active cell, animated with CSS. */
function useCapsule(rowRef: RefObject<HTMLDivElement | null>, activeIndex: number, count: number, deps: unknown[]) {
  const [box, setBox] = useState<{ left: number; width: number } | null>(null);
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row || activeIndex < 0) {
      setBox(null);
      return;
    }
    const measure = () => {
      const cell = row.children[activeIndex] as HTMLElement | undefined;
      if (!cell) return setBox(null);
      setBox({ left: cell.offsetLeft - row.scrollLeft + 3, width: cell.offsetWidth - 6 });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    row.addEventListener("scroll", measure, { passive: true });
    return () => {
      ro.disconnect();
      row.removeEventListener("scroll", measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, count, ...deps]);
  return box;
}

function Capsule({ box }: { box: { left: number; width: number } | null }) {
  if (!box) return null;
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute top-[5px] h-[54px] rounded-2xl transition-[transform,width] duration-300 ease-[cubic-bezier(.2,.8,.2,1)]"
      style={{ backgroundColor: M.accentSoft, width: box.width, transform: `translateX(${box.left}px)` }}
    />
  );
}

export default function XaleBar() {
  const navigate = useNavigate();
  const { tabs, activeTab, activeSegment } = useMobileNav();
  const unreadChat = useUnreadChatCount();
  const [hub, setHub] = useState<TabKey | null>(null);
  // A lead, the Add Lead sheet or the filter drawer covers the screen edge to
  // edge (the desktop's 100vw sheets); the bar steps out from under them.
  const covered = useAppSelector(
    (s) => s.leadFunctional.currentLeadId != null || Boolean(s.leadFunctional.isAddLead) || Boolean(s.leadFunctional.isFilterOpen),
  );

  // The hub shows only while its tab is the active one: tapping a tab with
  // segments navigates to one of them in the same event, so the hub and the
  // route land together, and leaving the tab (a drill, a notification) folds
  // the bar back without a reset.
  const hubTab = hub && hub === activeTab ? tabs.find((t) => t.key === hub) : null;
  const mainIndex = tabs.findIndex((t) => t.key === activeTab);
  const segIndex = hubTab ? hubTab.segments.findIndex((s) => s.key === activeSegment?.key) : -1;
  const mainRow = useRef<HTMLDivElement>(null);
  const segRow = useRef<HTMLDivElement>(null);
  const mainBox = useCapsule(mainRow, mainIndex, tabs.length, [hub == null]);
  const segBox = useCapsule(segRow, segIndex, hubTab?.segments.length ?? 0, [hub]);

  // Keep the active chip in view when the segments overflow.
  useEffect(() => {
    if (!hubTab || segIndex < 0) return;
    const cell = segRow.current?.children[segIndex] as HTMLElement | undefined;
    cell?.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
  }, [hubTab, segIndex]);

  const openTab = (tab: TabDef) => {
    if (tab.segments.length === 0) {
      setHub(null);
      navigate(tab.to);
      return;
    }
    const to = rememberedSegment(tab.key, tab.to);
    const known = tab.segments.some((s) => s.to === to) ? to : tab.to;
    setHub(tab.key);
    if (activeTab !== tab.key || !activeSegment) navigate(known);
  };
  const openSegment = (tab: TabDef, s: Segment) => {
    rememberSegment(tab.key, s.to);
    navigate(s.to);
  };

  if (covered) return null;
  return (
    <nav
      aria-label="Main"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[60]"
      style={{ paddingBottom: "max(8px, env(safe-area-inset-bottom))" }}
    >
      {/* Scrim: content fades before it reaches the glass. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[112px]"
        style={{ background: `linear-gradient(to bottom, rgba(244,247,245,0) 0%, ${M.bg} 78%)` }}
      />
      <div
        className="pointer-events-auto relative mx-[14px] h-16 overflow-hidden rounded-[26px] border bg-white/[0.86] shadow-[0_8px_20px_rgba(10,35,27,0.10)] backdrop-blur-xl"
        style={{ borderColor: "rgba(5,25,18,0.06)" }}
      >
        {hubTab ? (
          <div className="flex h-full items-center">
            <button
              type="button"
              onClick={() => setHub(null)}
              aria-label="Back to all tabs"
              className="ml-[6px] flex h-16 w-[44px] shrink-0 items-center justify-center active:scale-[0.92]"
            >
              <span className="flex size-[34px] items-center justify-center rounded-full" style={{ backgroundColor: M.greenDeep }}>
                <ChevronLeft size={19} color="#fff" strokeWidth={2.4} />
              </span>
            </button>
            <span aria-hidden className="h-[26px] w-px shrink-0" style={{ backgroundColor: M.border }} />
            <div className="relative flex h-full min-w-0 flex-1 items-stretch">
              <Capsule box={segBox} />
              <div ref={segRow} className="xm-noscroll flex h-full flex-1 items-stretch overflow-x-auto">
                {hubTab.segments.map((s) => (
                  <Cell
                    key={s.key}
                    icon={s.icon}
                    label={s.label}
                    active={activeSegment?.key === s.key}
                    onClick={() => openSegment(hubTab, s)}
                    className={cx("px-[9px]", hubTab.segments.length <= 5 ? "min-w-[64px] flex-1" : "min-w-[72px]")}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="relative h-full">
            <Capsule box={mainBox} />
            <div ref={mainRow} className="xm-noscroll flex h-full items-stretch overflow-x-auto">
              {tabs.map((t) => (
                <Cell
                  key={t.key}
                  icon={t.icon}
                  label={t.label}
                  active={activeTab === t.key}
                  dot={t.key === "chat" && unreadChat > 0}
                  onClick={() => openTab(t)}
                  className={tabs.length <= 5 ? "min-w-[58px] flex-1" : "min-w-[72px]"}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
